// Timestamp formatting for Wealth Secret Journal.
// Backend/DB stores UTC. Per user request the whole app renders in INDIAN STANDARD
// TIME (IST, Asia/Kolkata, UTC+5:30) regardless of the viewer's device timezone.

export const IST_TZ = "Asia/Kolkata";
export const IST_LABEL = "IST";

// Parse epoch (s or ms), numeric string, or ISO string into a Date.
// ISO strings WITHOUT a timezone designator are treated as UTC (backend stores UTC)
// to avoid double conversion; strings with Z or an explicit offset are respected as-is.
export function toDate(input) {
  if (input == null || input === "") return null;

  if (typeof input === "number") {
    return new Date(input < 1e12 ? input * 1000 : input);
  }

  const str = String(input).trim();

  // Pure epoch number in a string.
  if (/^\d+(\.\d+)?$/.test(str)) {
    const n = Number(str);
    return new Date(n < 1e12 ? n * 1000 : n);
  }

  // ISO-like date-time. If it lacks Z / +hh:mm / -hh:mm, it's a naive UTC value → add Z.
  const isoLike = str.replace(" ", "T");
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(isoLike);
  const hasTime = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoLike);
  const normalized = hasTime && !hasTz ? `${isoLike}Z` : isoLike;

  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Full date + time in IST (e.g. "14 Sep 2026, 8:02 PM IST").
export function fmtLocal(input, { withYear = true, withTime = true, withZone = true } = {}) {
  const d = toDate(input);
  if (!d) return "—";
  const opts = { day: "numeric", month: "short", timeZone: IST_TZ };
  if (withYear) opts.year = "numeric";
  if (withTime) { opts.hour = "2-digit"; opts.minute = "2-digit"; opts.hour12 = true; }
  const s = d.toLocaleString("en-IN", opts);
  return withTime && withZone ? `${s} ${IST_LABEL}` : s;
}

// Compact date + time in IST (no zone label), for dense lists.
export function fmtLocalShort(input) {
  const d = toDate(input);
  if (!d) return "";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST_TZ });
}

// ----- Epoch-second (UTC) → IST helpers (for the economic calendar / events) -----
const _fmt = (opts) => new Intl.DateTimeFormat("en-IN", { timeZone: IST_TZ, ...opts });
export const istTime = (ts) => (ts ? _fmt({ hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(ts * 1000)) : "—");
export const istDate = (ts) => (ts ? _fmt({ day: "numeric", month: "short" }).format(new Date(ts * 1000)) : "—");
export const istDateTime = (ts) => (ts ? _fmt({ day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(ts * 1000)) : "—");
export const istFull = (ts) => (ts ? _fmt({ weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(ts * 1000)) : "—");
// IST calendar day key "YYYY-MM-DD" (for day bucketing in IST).
export const istDayKey = (ts) => {
  if (!ts) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: IST_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ts * 1000));
};

// The timezone the app renders in.
export function localTimeZone() {
  return IST_TZ;
}
